"use client";

import { useState } from "react";
import { Link } from "@/i18n/routing";
import { Search, Package, MapPin, Calendar, Loader2, ShoppingBag, AlertCircle } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/Button";
import { formatPrice } from "@/lib/utils";

interface LookupOrder {
  orderNumber: string;
  status: string;
  total: number;
  subtotal: number;
  shippingCost: number;
  tax: number;
  discountAmount: number;
  shippingName: string;
  shippingLine1: string;
  shippingLine2: string | null;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
  deliveryMethod: string;
  createdAt: string;
  items: {
    id: string;
    name: string;
    quantity: number;
    price: number;
    totalPrice: number;
    image: string | null;
    slug: string | null;
  }[];
}

const statusStyles: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700",
  confirmed: "bg-blue-50 text-blue-700",
  processing: "bg-indigo-50 text-indigo-700",
  ready_for_pickup: "bg-purple-50 text-purple-700",
  out_for_delivery: "bg-cyan-50 text-cyan-700",
  shipped: "bg-cyan-50 text-cyan-700",
  delivered: "bg-emerald-50 text-emerald-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
  refunded: "bg-gray-50 text-gray-700",
};

const statusLabel = (s: string) =>
  s
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export default function OrderLookupPage() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState<LookupOrder | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) return;

    setLoading(true);
    setError("");
    setOrder(null);

    try {
      const params = new URLSearchParams({
        orderNumber: orderNumber.trim(),
        email: email.trim(),
      });
      const res = await fetch(`/api/orders/lookup?${params.toString()}`);
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Order not found");
        return;
      }

      setOrder(data);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Header />
    <div className="min-h-screen bg-white">
      <div className="container-luxury py-12 sm:py-16">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="mx-auto w-14 h-14 rounded-full bg-brand-gold/10 flex items-center justify-center mb-5">
              <Search className="h-6 w-6 text-brand-gold" />
            </div>
            <h1 className="font-display text-3xl text-brand-dark mb-2">
              Track Your Order
            </h1>
            <p className="text-sm text-brand-muted">
              Enter your order number and the email you used at checkout.
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSubmit} className="space-y-4 mb-8">
            <div>
              <label
                htmlFor="orderNumber"
                className="block text-xs font-medium uppercase tracking-widest text-brand-muted mb-2"
              >
                Order Number
              </label>
              <input
                id="orderNumber"
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="WIG-XXXXX-XXXX"
                className="w-full border border-gray-200 px-4 py-3 text-sm text-brand-dark placeholder:text-gray-300 focus:outline-none focus:border-brand-gold/50 transition-colors"
                required
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-medium uppercase tracking-widest text-brand-muted mb-2"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full border border-gray-200 px-4 py-3 text-sm text-brand-dark placeholder:text-gray-300 focus:outline-none focus:border-brand-gold/50 transition-colors"
                required
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-3 border border-red-100">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  Looking up...
                </>
              ) : (
                "Track Order"
              )}
            </Button>
          </form>

          {/* Order Result */}
          {order && (
            <div className="animate-fade-up border border-gray-100 divide-y divide-gray-100">
              {/* Order Header */}
              <div className="p-5 sm:p-6 flex items-center justify-between">
                <div>
                  <p className="text-xs tracking-widest uppercase text-brand-muted mb-1">
                    Order
                  </p>
                  <p className="text-sm font-medium text-brand-dark tracking-wider">
                    {order.orderNumber}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium capitalize ${
                    statusStyles[order.status] || "bg-gray-50 text-gray-700"
                  }`}
                >
                  {statusLabel(order.status)}
                </span>
              </div>

              {/* Items */}
              <div className="p-5 sm:p-6">
                <h3 className="text-xs font-medium uppercase tracking-widest text-brand-muted mb-4">
                  Items
                </h3>
                <div className="space-y-3">
                  {order.items.map((item) => (
                    <div key={item.id} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="relative w-10 h-10 bg-brand-cream flex items-center justify-center flex-shrink-0">
                          <ShoppingBag className="h-3.5 w-3.5 text-brand-gold/30" />
                          {item.quantity > 1 && (
                            <span className="absolute -top-1 -end-1 flex items-center justify-center h-4 w-4 rounded-full bg-brand-dark text-white text-[10px] font-bold">
                              {item.quantity}
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-brand-dark">{item.name}</span>
                      </div>
                      <span className="text-sm text-brand-dark">
                        {formatPrice(item.totalPrice)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Address */}
              <div className="p-5 sm:p-6">
                <h3 className="text-xs font-medium uppercase tracking-widest text-brand-muted mb-3">
                  Shipping To
                </h3>
                <div className="flex items-start space-x-3">
                  <MapPin className="h-4 w-4 text-brand-gold mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-brand-dark space-y-0.5">
                    <p className="font-medium">{order.shippingName}</p>
                    <p>
                      {order.shippingLine1}
                      {order.shippingLine2 ? `, ${order.shippingLine2}` : ""}
                    </p>
                    <p>
                      {order.shippingCity}, {order.shippingState} {order.shippingZip}
                    </p>
                  </div>
                </div>
              </div>

              {/* Total + Date */}
              <div className="p-5 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-brand-muted">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>
                      {new Date(order.createdAt).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <div className="text-end">
                    <span className="text-xs uppercase tracking-widest text-brand-muted me-2">
                      Total
                    </span>
                    <span className="text-lg font-display text-brand-dark">
                      {formatPrice(order.total)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Delivery info */}
              <div className="p-5 sm:p-6 bg-gray-50">
                <div className="flex items-center space-x-3">
                  <Package className="h-5 w-5 text-brand-gold" />
                  <div>
                    <p className="text-sm font-medium text-brand-dark">
                      {order.deliveryMethod === "uber" || order.deliveryMethod === "uber_direct"
                        ? "Uber Direct Delivery"
                        : "Standard Shipping"}
                    </p>
                    <p className="text-xs text-brand-muted mt-0.5">
                      {order.deliveryMethod === "uber" || order.deliveryMethod === "uber_direct"
                        ? "Same-day delivery"
                        : "Estimated 3-5 business days"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer link */}
          <div className="text-center mt-8">
            <p className="text-sm text-brand-muted">
              Have an account?{" "}
              <Link href="/account/orders" className="text-brand-gold hover:underline">
                Sign in to view all orders
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
    <Footer />
    </>
  );
}
